import { GUIDE_CATEGORIES, getGuideCategory } from "./categories";
import { GUIDE_ARTICLES } from "./articles";
import type { GuideArticle, GuideCategory } from "./types";

export type { GuideArticle, GuideCategory, CommercialLink, FaqItem } from "./types";
export { GUIDE_CATEGORIES, getGuideCategory } from "./categories";
export { GUIDE_ARTICLES } from "./articles";

const BRAND_TOKEN = /\{brandName\}/g;

/** Resolve editorial brand tokens at render time so a runtime cutover is atomic. */
export function resolveGuideBrandText(value: string, brandName: string): string {
  return value.replace(BRAND_TOKEN, brandName);
}

function resolveGuideValue<T>(value: T, brandName: string): T {
  if (typeof value === "string") {
    return resolveGuideBrandText(value, brandName) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => resolveGuideValue(item, brandName)) as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, resolveGuideValue(item, brandName)]),
    ) as T;
  }
  return value;
}

export function resolveGuideArticleBrand(
  article: GuideArticle,
  brandName: string,
): GuideArticle {
  return resolveGuideValue(article, brandName);
}

export function resolveGuideCategoryBrand(
  category: GuideCategory,
  brandName: string,
): GuideCategory {
  return resolveGuideValue(category, brandName);
}

const ARTICLE_BY_SLUG = new Map(GUIDE_ARTICLES.map((a) => [a.slug, a]));

export function getGuideArticle(slug: string): GuideArticle | undefined {
  return ARTICLE_BY_SLUG.get(slug);
}

/** Articles belonging to a category, in publication order (newest first). */
export function getArticlesByCategory(categorySlug: string): GuideArticle[] {
  return GUIDE_ARTICLES.filter((a) => a.category === categorySlug).sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt),
  );
}

/** All articles, newest first — used by the hub index. */
export function getAllGuideArticles(): GuideArticle[] {
  return [...GUIDE_ARTICLES].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/** Resolve related article slugs to article records, dropping any unknowns. */
export function getRelatedArticles(article: GuideArticle): GuideArticle[] {
  return (article.related || [])
    .map((slug) => ARTICLE_BY_SLUG.get(slug))
    .filter((a): a is GuideArticle => Boolean(a));
}

export function getCategoryForArticle(article: GuideArticle): GuideCategory | undefined {
  return getGuideCategory(article.category);
}

/**
 * Every canonical Guide Hub path — the hub index, each category page, and each
 * article. Mirrored by the api-server (`content/guide-slugs.ts`) for the
 * sitemap; the sync test keeps the two in lockstep.
 */
export function listGuidePagePaths(): string[] {
  return [
    "/guides",
    ...GUIDE_CATEGORIES.map((c) => `/guides/category/${c.slug}`),
    ...GUIDE_ARTICLES.map((a) => `/guides/${a.slug}`),
  ];
}
